import { useEffect, useMemo, useRef, useState } from 'react';

// 🧾 Pedido no GIX (14/10/2026, Compras Revenda do Evandro).
// Na Negociação o comprador monta a OC que o Escalone cria SOZINHO no GIX (movimento E01)
// quando a demanda é aprovada: fornecedor, condição de pagamento, comprador no GIX, tipo de
// despesa, frete e os itens com código do GIX, quantidade e preço. O último preço pago em
// OC E01 aparece ao lado de cada item. Tudo fica em card.campos.__pedidoGix; nas etapas
// seguintes o mesmo quadro aparece só para leitura.

export interface ItemPedido { produto: number; descricao: string; unidade: string; quantidade: number; preco: number }
export interface PedidoGixDados {
  fornecedor?: { codigo: number; descricao: string } | null;
  condicao?: { codigo: number; descricao: string } | null;
  comprador?: { codigo: number; usuario: string; nome: string } | null;
  tipoDespesa?: { codigo: number; descricao: string } | null;
  centroCusto?: number;
  frete?: 'C' | 'F';
  itens: ItemPedido[];
}

interface Props {
  inicial?: PedidoGixDados | null;
  produtosPainel?: number[];
  nomeResponsavel?: string;
  somenteLeitura?: boolean;
  onMudar: (p: PedidoGixDados) => void;
}

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const sem = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();

// o que falta para a OC sair (usado também na trava de saída da Negociação)
export const faltasDoPedido = (p?: PedidoGixDados | null): string[] => {
  const f: string[] = [];
  if (!p?.fornecedor) f.push('fornecedor');
  if (!p?.condicao) f.push('condição de pagamento');
  if (!p?.comprador) f.push('comprador no GIX');
  if (!p?.tipoDespesa) f.push('tipo de despesa');
  if (!p?.itens?.length) f.push('itens');
  else if (p.itens.some(i => !(i.quantidade > 0) || !(i.preco > 0))) f.push('quantidade e preço de todos os itens');
  return f;
};

function Busca({ rota, placeholder, onEscolher, disabled }: { rota: string; placeholder: string; disabled?: boolean; onEscolher: (x: { codigo: number; descricao: string; unidade?: string }) => void }) {
  const [q, setQ] = useState('');
  const [res, setRes] = useState<{ codigo: number; descricao: string; unidade?: string }[]>([]);
  const t = useRef<any>(null);
  useEffect(() => {
    clearTimeout(t.current);
    if (q.trim().length < 2) { setRes([]); return; }
    t.current = setTimeout(() => {
      fetch(`/api/gix/${rota}?q=${encodeURIComponent(q.trim())}&limit=12`).then(r => r.json())
        .then(d => setRes(d.results || [])).catch(() => setRes([]));
    }, 300);
  }, [q, rota]);
  return (
    <div className="relative">
      <input value={q} disabled={disabled} onChange={e => setQ(e.target.value)} placeholder={placeholder}
        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 disabled:bg-gray-50" />
      {res.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {res.map(x => <button key={x.codigo} type="button" onClick={() => { onEscolher(x); setQ(''); setRes([]); }}
            className="w-full text-left px-3 py-1.5 text-[13px] hover:bg-gray-50"><span className="text-gray-500 mr-2">{x.codigo}</span>{x.descricao}</button>)}
        </div>)}
    </div>
  );
}

export default function PedidoGix({ inicial, produtosPainel = [], nomeResponsavel = '', somenteLeitura, onMudar }: Props) {
  const [p, setP] = useState<PedidoGixDados>(() => ({ frete: 'C', centroCusto: 27, itens: [], ...(inicial || {}) }));
  const [sug, setSug] = useState<any>(null);
  const primeira = useRef(true);

  const codigos = useMemo(() => Array.from(new Set([...p.itens.map(i => i.produto), ...(p.itens.length ? [] : produtosPainel)])), [p.itens, produtosPainel]);

  useEffect(() => {
    fetch(`/api/gix/revenda/sugestoes?produtos=${codigos.join(',')}&fornecedor=${p.fornecedor?.codigo || 0}`)
      .then(r => r.json()).then(d => { if (d.status === 'ok') setSug(d); }).catch(() => {});
  }, [codigos.join(','), p.fornecedor?.codigo]); // eslint-disable-line react-hooks/exhaustive-deps

  // primeira carga: itens do painel de estoque, comprador pelo nome do responsável e tipo de despesa sugerido
  useEffect(() => {
    if (!sug || somenteLeitura || !primeira.current) return;
    primeira.current = false;
    setP(atual => {
      const novo = { ...atual };
      if (!novo.itens.length && sug.produtos?.length)
        novo.itens = sug.produtos.map((x: any) => ({ produto: x.produto, descricao: x.descricao, unidade: x.unidade || 'PC', quantidade: 0, preco: x.ultimo?.preco || 0 }));
      if (!novo.comprador && nomeResponsavel) {
        const partes = sem(nomeResponsavel).split(' ').filter(w => w.length > 2);
        const achado = (sug.compradores || []).find((c: any) => partes.length >= 2 && partes.slice(0, 2).every(w => sem(c.nome || '').includes(w)));
        if (achado) novo.comprador = { codigo: achado.codigo, usuario: achado.usuario, nome: achado.nome };
      }
      if (!novo.tipoDespesa && sug.tipo_despesa_sugerido) novo.tipoDespesa = { codigo: sug.tipo_despesa_sugerido.codigo, descricao: sug.tipo_despesa_sugerido.descricao };
      return novo;
    });
  }, [sug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (!somenteLeitura) onMudar(p); }, [p]); // eslint-disable-line react-hooks/exhaustive-deps

  const muda = (parcial: Partial<PedidoGixDados>) => setP(a => ({ ...a, ...parcial }));
  const mudaItem = (k: number, parcial: Partial<ItemPedido>) => setP(a => ({ ...a, itens: a.itens.map((it, i) => i === k ? { ...it, ...parcial } : it) }));
  const ultimo = (cod: number) => sug?.produtos?.find((x: any) => x.produto === cod)?.ultimo;
  const total = p.itens.reduce((s, i) => s + (i.quantidade || 0) * (i.preco || 0), 0);
  const faltas = faltasDoPedido(p);
  const compradores = useMemo(() => {
    const vistos = new Set<number>();
    return (sug?.compradores || []).filter((c: any) => !vistos.has(c.codigo) && vistos.add(c.codigo));
  }, [sug]);
  const ro = !!somenteLeitura;
  const rotulo = 'text-[11px] uppercase tracking-wide text-gray-500 font-medium';
  const escolhido = (texto: string, limpar: () => void) => (
    <div className="flex items-center gap-2 text-sm border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
      <span className="flex-1 truncate">{texto}</span>
      {!ro && <button type="button" onClick={limpar} className="text-gray-400 hover:text-red-600" title="Trocar">✕</button>}
    </div>
  );

  return (
    <div className="border border-gray-200/80 rounded-xl overflow-hidden bg-white shadow-sm ring-1 ring-gray-900/5">
      <div className="flex items-center gap-2.5 px-3 py-2.5 bg-slate-50 border-b border-slate-200">
        <div className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center"><span className="text-white text-xs">🧾</span></div>
        <div className="flex-1">
          <h3 className="font-bold text-gray-900 text-sm tracking-tight">Pedido no GIX</h3>
          <span className="text-[10px] text-gray-500 font-medium">a OC E01 sai sozinha no GIX quando a demanda for aprovada</span>
        </div>
        <span className="text-sm font-semibold text-gray-900">{brl(total)}</span>
      </div>
      <div className="p-3 space-y-3">
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          <div className="space-y-1"><div className={rotulo}>Fornecedor</div>
            {p.fornecedor ? escolhido(`${p.fornecedor.codigo} · ${p.fornecedor.descricao}`, () => muda({ fornecedor: null }))
              : <Busca rota="fornecedores" placeholder="Procurar fornecedor no GIX…" disabled={ro} onEscolher={x => muda({ fornecedor: { codigo: x.codigo, descricao: x.descricao } })} />}
          </div>
          <div className="space-y-1"><div className={rotulo}>Condição de pagamento</div>
            {p.condicao ? escolhido(`${p.condicao.codigo} · ${p.condicao.descricao}`, () => muda({ condicao: null }))
              : <Busca rota="condicoes" placeholder="Ex.: 30/60/90…" disabled={ro} onEscolher={x => muda({ condicao: { codigo: x.codigo, descricao: x.descricao } })} />}
          </div>
          <div className="space-y-1"><div className={rotulo}>Comprador no GIX</div>
            <select disabled={ro} value={p.comprador?.codigo || ''} className="w-full text-sm border border-gray-200 rounded-lg px-2 py-2 bg-white disabled:bg-gray-50"
              onChange={e => { const c = compradores.find((x: any) => String(x.codigo) === e.target.value); muda({ comprador: c ? { codigo: c.codigo, usuario: c.usuario, nome: c.nome } : null }); }}>
              <option value="">Escolher…</option>
              {p.comprador && !compradores.some((c: any) => c.codigo === p.comprador!.codigo) && <option value={p.comprador.codigo}>{p.comprador.nome}</option>}
              {compradores.map((c: any) => <option key={c.codigo} value={c.codigo}>{c.nome} ({c.usuario})</option>)}
            </select>
          </div>
          <div className="space-y-1"><div className={rotulo}>Tipo de despesa</div>
            <select disabled={ro} value={p.tipoDespesa?.codigo || ''} className="w-full text-sm border border-gray-200 rounded-lg px-2 py-2 bg-white disabled:bg-gray-50"
              onChange={e => { const t = (sug?.tipos_despesa || []).find((x: any) => String(x.codigo) === e.target.value); muda({ tipoDespesa: t ? { codigo: t.codigo, descricao: t.descricao } : null }); }}>
              <option value="">Escolher…</option>
              {p.tipoDespesa && !(sug?.tipos_despesa || []).some((t: any) => t.codigo === p.tipoDespesa!.codigo) && <option value={p.tipoDespesa.codigo}>{p.tipoDespesa.descricao}</option>}
              {(sug?.tipos_despesa || []).map((t: any) => <option key={t.codigo} value={t.codigo}>{t.descricao}</option>)}
            </select>
          </div>
          <div className="space-y-1"><div className={rotulo}>Frete</div>
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
              {([['C', 'CIF (fornecedor paga)'], ['F', 'FOB (nós pagamos)']] as const).map(([v, t]) => (
                <button key={v} type="button" disabled={ro} onClick={() => muda({ frete: v })}
                  className={`flex-1 px-2 py-2 ${p.frete === v ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>{t}</button>))}
            </div>
          </div>
        </div>

        <div className="space-y-1">
          <div className={rotulo}>Itens da OC</div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead><tr className="text-left text-[10px] uppercase tracking-wide text-gray-500 bg-gray-50">
                <th className="px-2 py-1.5">Produto</th><th className="px-2 py-1.5 text-right w-24">Qtd</th>
                <th className="px-2 py-1.5 text-right w-28">Preço unit.</th><th className="px-2 py-1.5 text-right w-28">Total</th>
                <th className="px-2 py-1.5">Último preço E01</th>{!ro && <th className="w-6"></th>}</tr></thead>
              <tbody>
                {p.itens.map((it, k) => {
                  const u = ultimo(it.produto);
                  return (
                    <tr key={it.produto} className="border-t border-gray-100">
                      <td className="px-2 py-1.5"><span className="text-gray-500 mr-1">{it.produto}</span>{it.descricao}</td>
                      <td className="px-2 py-1.5 text-right">
                        <input type="number" min={0} step="any" disabled={ro} value={it.quantidade || ''} onChange={e => mudaItem(k, { quantidade: Number(e.target.value) })}
                          className="w-20 text-right border border-gray-200 rounded px-1.5 py-1 disabled:bg-gray-50" /> <span className="text-[11px] text-gray-500">{it.unidade}</span></td>
                      <td className="px-2 py-1.5 text-right">
                        <input type="number" min={0} step="0.01" disabled={ro} value={it.preco || ''} onChange={e => mudaItem(k, { preco: Number(e.target.value) })}
                          className="w-24 text-right border border-gray-200 rounded px-1.5 py-1 disabled:bg-gray-50" /></td>
                      <td className="px-2 py-1.5 text-right font-medium">{brl((it.quantidade || 0) * (it.preco || 0))}</td>
                      <td className="px-2 py-1.5 text-[12px] text-gray-600">
                        {u ? <span title={`OC ${u.oc} · empresa ${u.empresa} · ${u.fornecedor_nome || ''}`}>{brl(u.preco)} · {u.data?.split('-').reverse().join('/')}
                          {it.preco > 0 && u.preco > 0 && Math.abs(it.preco / u.preco - 1) >= 0.005 && (
                            <b className={it.preco > u.preco ? 'text-red-600 ml-1' : 'text-emerald-700 ml-1'}>{it.preco > u.preco ? '+' : ''}{((it.preco / u.preco - 1) * 100).toFixed(1)}%</b>)}
                        </span> : <span className="text-gray-400">sem compra E01</span>}
                      </td>
                      {!ro && <td><button type="button" onClick={() => setP(a => ({ ...a, itens: a.itens.filter((_, i) => i !== k) }))} className="text-gray-400 hover:text-red-600">✕</button></td>}
                    </tr>);
                })}
              </tbody>
            </table>
          </div>
          {!ro && <Busca rota="produtos" placeholder="Acrescentar produto do GIX (código ou descrição)…"
            onEscolher={x => setP(a => a.itens.some(i => i.produto === x.codigo) ? a : ({ ...a, itens: [...a.itens, { produto: x.codigo, descricao: x.descricao, unidade: x.unidade || 'PC', quantidade: 0, preco: 0 }] }))} />}
        </div>
        {faltas.length > 0 && !ro && <div className="text-xs text-amber-700">Para a OC sair sozinha, falta: {faltas.join(', ')}.</div>}
      </div>
    </div>
  );
}
